/* CareMatters II – Personal History Interview */
const questions: Record<string, PolicyFormQuestion> = {
  financialProfessionalName: {
    id: 'financial-professional-name',
    questionRef: 'financialProfessionalName',
    title: 'Financial professional name',
    type: 'text',
    required: true,
  },
  telephoneNumber: {
    id: 'telephone-number',
    questionRef: 'telephoneNumber',
    title: 'Telephone number',
    type: 'text',
    required: true,
    validationType: 'phone',
  },
  email: {
    id: 'email',
    questionRef: 'email',
    title: 'Email',
    type: 'text',
    required: true,
    validationType: 'email',
  },
  age: {
    id: 'age',
    questionRef: 'age',
    title: 'Age',
    type: 'text',
    required: true,
    validationType: 'age',
  },
  sex: {
    id: 'sex',
    questionRef: 'sex',
    title: 'Sex',
    type: 'textselect',
    required: true,
    options: [
      { id: 'male', value: 'Male' },
      { id: 'female', value: 'Female' },
      { id: 'intersex', value: 'Intersex'}
    ],
  },
  height: {
    id: 'height',
    questionRef: 'height',
    title: 'Height',
    type: 'text',
    required: true,
    validationType: 'height',
  },
  weight: {
    id: 'weight',
    questionRef: 'weight',
    title: 'Weight',
    type: 'text',
    required: true,
    validationType: 'weight',
  },
  weightLoss: {
    id: 'weight-loss',
    questionRef: 'weightLoss',
    title: 'Any weight loss in the past 6 months (not due to diet or exercise)?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  numPounds: {
    id: 'num-pounds',
    questionRef: 'numPounds',
    title: 'How many pounds?',
    type: 'number',
    required: true,
    conditional: {
      visible: {
        field: 'weightLoss',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  reasonWeightLoss: {
    id: 'reason-weight-loss',
    questionRef: 'reasonWeightLoss',
    title: 'Reason for weight loss',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'weightLoss',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  tobacco: {
    id: 'tobacco',
    questionRef: 'tobacco',
    title:
      'Tobacco (i.e., cigarettes/e-cigarettes, cigars, pipes, hookahs, nicotine products (gum, patches, etc.)',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalMarijuana: {
    id: 'medical-marijuana',
    questionRef: 'medicalMarijuana',
    title: 'Medical marijuana',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  medicalMarijuanaCondition: {
    id: 'medical-marijuana-condition',
    questionRef: 'medicalMarijuanaCondition',
    title: 'If yes, what medical condition are you using it for?',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'medicalMarijuana',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  previouslyDeclined: {
    id: 'previously-declined',
    questionRef: 'previouslyDeclined',
    title: 'Previously declined for life or LTC insurance?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  declineDetails: {
    id: 'decline-details',
    questionRef: 'declineDetails',
    title: 'If yes, provide date and reason for decline:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'previouslyDeclined',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  medicationsList: {
    id: 'medications-list',
    questionRef: 'medicationsList',
    title: 'Provide list of medications and medical condition being treated.',
    type: 'text',
    required: false,
  },
  providerRecommendations: {
    id: 'provider-recommendations',
    questionRef: 'providerRecommendations',
    title:
      'Have any of your providers recommended medications or treatments that you have decided against?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  providerRecommendationDetails: {
    id: 'provider-recommendation-details',
    questionRef: 'providerRecommendationDetails',
    title: 'If yes, provide details:',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'providerRecommendations',
        operator: 'equals',
        value: 'yes',
      },
      required: {
        field: 'providerRecommendations',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  heartDisease: {
    id: 'heart-disease',
    questionRef: 'heartDisease',
    title: 'Heart disease:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  heartDiseaseDetails: {
    id: 'heart-disease-details',
    questionRef: 'heartDiseaseDetails',
    title: 'If yes, describe:',
    type: 'text',
    required: false,
    conditional: {
      visible: {
        field: 'heartDisease',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  hypertension: {
    id: 'hypertension',
    questionRef: 'hypertension',
    title: 'Hypertension:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  hypertensionDetails: {
    id: 'hypertension-details',
    questionRef: 'hypertensionDetails',
    title: 'If yes, provide last blood pressure reading:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'hypertension',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  tiaStroke: {
    id: 'tia-stroke',
    questionRef: 'tiaStroke',
    title: 'TIA/Stroke:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  tiaStrokeDiagnosisDate: {
    id: 'tia-stroke-diagnosis-date',
    questionRef: 'tiaStrokeDiagnosisDate',
    title: 'If yes, date of diagnosis:',
    type: 'date',
    required: true,
    conditional: {
      visible: {
        field: 'tiaStroke',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  bloodClots: {
    id: 'blood-clots',
    questionRef: 'bloodClots',
    title: 'Blood clots:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  bloodClotsDiagnosisDate: {
    id: 'blood-clots-diagnosis-date',
    questionRef: 'bloodClotsDiagnosisDate',
    title: 'If yes, date of diagnosis:',
    type: 'date',
    required: true,
    conditional: {
      visible: {
        field: 'bloodClots',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  diabetesType2: {
    id: 'diabetes-type-2',
    questionRef: 'diabetesType2',
    title: 'Diabetes type 2:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  diabetesA1c: {
    id: 'diabetes-a1c',
    questionRef: 'diabetesA1c',
    title: 'If yes, provide A1C:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'diabetesType2',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  diabetesComplications: {
    id: 'diabetes-complications',
    questionRef: 'diabetesComplications',
    title: 'If yes, any nephropathy, neuropathy, retinopathy, foot sores or ulcers:',
    type: 'radio',
    required: true,
    conditional: {
      visible: {
        field: 'diabetesType2',
        operator: 'equals',
        value: 'yes',
      },
    },
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  arthritis: {
    id: 'arthritis',
    questionRef: 'arthritis',
    title: 'Any form of arthritis:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  arthritisType: {
    id: 'arthritis-type',
    questionRef: 'arthritisType',
    title: 'If yes, provide type:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'arthritis',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  cancerHistory: {
    id: 'cancer-history',
    questionRef: 'cancerHistory',
    title: 'Any history of cancer:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  cancerDetails: {
    id: 'cancer-details',
    questionRef: 'cancerDetails',
    title:
      'If yes, provide details (i.e., type of cancer, date of diagnosis, treatment, stage or grade):',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'cancerHistory',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  depressionAnxiety: {
    id: 'depression-anxiety',
    questionRef: 'depressionAnxiety',
    title: 'Any depression, anxiety, or the "blues, loss of interest in doing things?"',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  depressionOnsetDate: {
    id: 'depression-onset-date',
    questionRef: 'depressionOnsetDate',
    title: 'If yes, provide date of onset:',
    type: 'date',
    required: true,
    conditional: {
      visible: {
        field: 'depressionAnxiety',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  depressionMedicationChangeDate: {
    id: 'depression-medication-change-date',
    questionRef: 'depressionMedicationChangeDate',
    title: 'Date of last change in medication:',
    type: 'date',
    required: false,
    conditional: {
      visible: {
        field: 'depressionAnxiety',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  assistiveDevices: {
    id: 'assistive-devices',
    questionRef: 'assistiveDevices',
    title: 'Do you use any assistive devices for mobility?',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  assistiveDevicesExplain: {
    id: 'assistive-devices-explain',
    questionRef: 'assistiveDevicesExplain',
    title: 'If yes, explain:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'assistiveDevices',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  memoryLoss: {
    id: 'memory-loss',
    questionRef: 'memoryLoss',
    title: 'Any memory loss, forgetfulness or cognitive concerns:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  memoryLossExplain: {
    id: 'memory-loss-explain',
    questionRef: 'memoryLossExplain',
    title: 'If yes, explain:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'memoryLoss',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  memoryLossPhysician: {
    id: 'memory-loss-physician',
    questionRef: 'memoryLossPhysician',
    title: 'If yes, have you discussed with your physician:',
    type: 'radio',
    required: true,
    conditional: {
      visible: {
        field: 'memoryLoss',
        operator: 'equals',
        value: 'yes',
      },
    },
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  physicalTherapy: {
    id: 'physical-therapy',
    questionRef: 'physicalTherapy',
    title: 'Any physical therapy in the past 6 months:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  physicalTherapyExplain: {
    id: 'physical-therapy-explain',
    questionRef: 'physicalTherapyExplain',
    title: 'If yes, explain:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'physicalTherapy',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  injections: {
    id: 'injections',
    questionRef: 'injections',
    title: 'Any injections in the past 12 months:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  injectionsExplain: {
    id: 'injections-explain',
    questionRef: 'injectionsExplain',
    title: 'If yes, explain:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'injections',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  surgeries: {
    id: 'surgeries',
    questionRef: 'surgeries',
    title: 'Any surgeries in the past 12 months:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  surgeriesExplain: {
    id: 'surgeries-explain',
    questionRef: 'surgeriesExplain',
    title: 'If yes, explain:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'surgeries',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  plannedSurgery: {
    id: 'planned-surgery',
    questionRef: 'plannedSurgery',
    title: 'Any planned or recommended surgery in the next 12 months:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  plannedSurgeryExplain: {
    id: 'planned-surgery-explain',
    questionRef: 'plannedSurgeryExplain',
    title: 'If yes, explain:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'plannedSurgery',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  hospitalizations: {
    id: 'hospitalizations',
    questionRef: 'hospitalizations',
    title: 'Any hospitalizations in the past 12 months:',
    type: 'radio',
    required: true,
    options: [
      { id: 'yes', value: 'Yes' },
      { id: 'no', value: 'No' },
    ],
  },
  hospitalizationsExplain: {
    id: 'hospitalizations-explain',
    questionRef: 'hospitalizationsExplain',
    title: 'If yes, explain:',
    type: 'text',
    required: true,
    conditional: {
      visible: {
        field: 'hospitalizations',
        operator: 'equals',
        value: 'yes',
      },
    },
  },
  weeklyActivities: {
    id: 'weekly-activities',
    questionRef: 'weeklyActivities',
    title:
      'How many times a week would you estimate the proposed insured leaves their home for normal errands/activities (i.e., store, outings with friends, exercise, etc.)',
    type: 'radio',
    required: true,
    options: [
      { id: '0-1-times', value: '0-1 times a week' },
      { id: '2-4-times', value: '2-4 times a week' },
      { id: '5-6-times', value: '5-6 times a week' },
      { id: 'several-times-daily', value: 'Leaves home several times a day' },
    ],
  },
};

export const nationwideFormDefinition: PolicyFormDefinition = {
  sections: [
    /* 1 ▸ Contact Information --------------------------------------------------- */
    {
      id: 'contact-info',
      title: 'Contact Information',
      subsections: [
        {
          id: 'info',
          title: 'Contact Information',
          questionGroups: [
            {
              id: 'name-info',
              title: 'Name',
              format: 'default',
              questions: ['financialProfessionalName'],
            },
            {
              id: 'contact-info',
              title: 'Contact Information',
              format: 'grid',
              questions: ['telephoneNumber', 'email'],
              className: 'grid grid-cols-1 md:grid-cols-2 gap-6 items-start'
            },
          ],
        },
      ],
    },

    /* 2 ▸ Client Info Providers -------------------------------------------------- */
    {
      id: 'client-info',
      title: 'Client Information',
      subsections: [
        {
          id: 'client',
          title: 'Client Information',
          questionGroups: [
            {
              id: 'client-information',
              format: 'grid',
              questions: ['age', 'sex', 'height', 'weight'],
            },
            {
              id: 'weight-information',
              format: 'default',
              questions: ['weightLoss', 'numPounds', 'reasonWeightLoss'],
            },
          ],
        },
      ],
    },

    /* 3 ▸ Smoking ------------------------------------------------------------ */
    {
      id: 'smoking',
      title: 'Tobacco & Medical Marijuana',
      subsections: [
        {
          id: 'tobacco-medical-marijuana',
          title: 'Tobacco & Medical Marijuana',
          questionGroups: [
            {
              id: 'tobacco-group',
              format: 'default',
              questions: ['tobacco'],
            },
            {
              id: 'marijuana-group',
              format: 'default',
              questions: ['medicalMarijuana', 'medicalMarijuanaCondition'],
            },
          ],
        },
      ],
    },

    /* 4 ▸ Eligibility ------------------------------------------------------------ */
    {
      id: 'eligibility',
      title: 'Insurance Eligibility History',
      subsections: [
        {
          id: 'life-ltc-history',
          title: 'Life/LTC Eligibility History',
          questionGroups: [
            {
              id: 'life-ltc-group',
              format: 'default',
              questions: ['previouslyDeclined', 'declineDetails'],
            },
          ],
        },
      ],
    },

    /* 5 ▸ Medications & Medical History ------------------------------------------ */
    {
      id: 'medications-medical',
      title: 'Medications & Medical History',
      subsections: [
        {
          id: 'medications',
          title: 'Medications & Medical Conditions',
          questionGroups: [
            {
              id: 'medications-group',
              format: 'default',
              questions: [
                'medicationsList',
                'providerRecommendations',
                'providerRecommendationDetails',
              ],
            },
          ],
        },
        {
          id: 'heart-conditions',
          title: 'Heart & Cardiovascular',
          questionGroups: [
            {
              id: 'heart-group',
              format: 'default',
              questions: [
                'heartDisease',
                'heartDiseaseDetails',
                'hypertension',
                'hypertensionDetails',
              ],
            },
          ],
        },
        {
          id: 'neurological-conditions',
          title: 'Neurological Conditions',
          questionGroups: [
            {
              id: 'neurological-group',
              format: 'default',
              questions: [
                'tiaStroke',
                'tiaStrokeDiagnosisDate',
                'bloodClots',
                'bloodClotsDiagnosisDate',
              ],
            },
          ],
        },
        {
          id: 'chronic-conditions',
          title: 'Chronic Conditions',
          questionGroups: [
            {
              id: 'diabetes-group',
              format: 'default',
              questions: ['diabetesType2', 'diabetesA1c', 'diabetesComplications'],
            },
            {
              id: 'arthritis-group',
              format: 'default',
              questions: ['arthritis', 'arthritisType'],
            },
          ],
        },
        {
          id: 'cancer-history',
          title: 'Cancer History',
          questionGroups: [
            {
              id: 'cancer-group',
              format: 'default',
              questions: ['cancerHistory', 'cancerDetails'],
            },
          ],
        },
      ],
    },

    /* 7 ▸ Mental Health & Cognitive Function ------------------------------------- */
    {
      id: 'mental-health',
      title: 'Mental Health & Cognitive Function',
      subsections: [
        {
          id: 'depression-anxiety',
          title: 'Depression & Anxiety',
          questionGroups: [
            {
              id: 'depression-group',
              format: 'default',
              questions: [
                'depressionAnxiety',
                'depressionOnsetDate',
                'depressionMedicationChangeDate',
              ],
            },
          ],
        },
        {
          id: 'cognitive-function',
          title: 'Memory & Cognitive Function',
          questionGroups: [
            {
              id: 'memory-group',
              format: 'default',
              questions: ['memoryLoss', 'memoryLossExplain', 'memoryLossPhysician'],
            },
          ],
        },
      ],
    },

    /* 8 ▸ Mobility & Assistive Devices ------------------------------------------- */
    {
      id: 'mobility',
      title: 'Mobility & Assistive Devices',
      subsections: [
        {
          id: 'assistive-devices',
          title: 'Assistive Devices',
          questionGroups: [
            {
              id: 'devices-group',
              format: 'default',
              questions: ['assistiveDevices', 'assistiveDevicesExplain'],
            },
          ],
        },
      ],
    },

    /* 9 ▸ Recent Medical Care ---------------------------------------------------- */
    {
      id: 'recent-medical-care',
      title: 'Recent Medical Care',
      subsections: [
        {
          id: 'therapy-injections',
          title: 'Physical Therapy & Injections',
          questionGroups: [
            {
              id: 'therapy-group',
              format: 'default',
              questions: ['physicalTherapy', 'physicalTherapyExplain'],
            },
            {
              id: 'injections-group',
              format: 'default',
              questions: ['injections', 'injectionsExplain'],
            },
          ],
        },
        {
          id: 'surgeries-hospitalizations',
          title: 'Surgeries & Hospitalizations',
          questionGroups: [
            {
              id: 'surgeries-group',
              format: 'default',
              questions: [
                'surgeries',
                'surgeriesExplain',
                'plannedSurgery',
                'plannedSurgeryExplain',
              ],
            },
            {
              id: 'hospitalizations-group',
              format: 'default',
              questions: ['hospitalizations', 'hospitalizationsExplain'],
            },
          ],
        },
      ],
    },

    /* 10 ▸ Daily Activities ------------------------------------------------------ */
    {
      id: 'daily-activities',
      title: 'Daily Activities & Independence',
      subsections: [
        {
          id: 'weekly-activities',
          title: 'Weekly Activities',
          questionGroups: [
            {
              id: 'activities-group',
              format: 'default',
              questions: ['weeklyActivities'],
            },
          ],
        },
      ],
    },
  ],

  /* ---------- Question record -------------------------------------------------- */
  questions,
};
